import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
} from 'react';
import { Menu, X } from 'lucide-react';

import { localeToPath } from '~/i18n/config';
import { useContent, useLocale } from '~/hooks/useContent';
import { useScrollSpy } from '~/hooks/useScrollSpy';
import { visibleNavLinks } from '~/lib/navLinks';
import { Button } from '~/components/ui/Button';
import { Container } from '~/components/ui/Container';
import { LanguageSwitcher } from './LanguageSwitcher';
import { cn } from '~/lib/cn';

import logoUrl from '~/assets/logos/arpa-logo.svg';

/**
 * Sticky, accessible primary navigation bar.
 *
 * Transparent at top → backdrop-blurred with deep-panel background after
 * 80 px of scroll. Mobile (<1024 px) collapses to hamburger + full-screen drawer.
 *
 * Scroll-state is initialised to false on both server and client to prevent
 * hydration mismatches; the scroll listener attaches in useEffect.
 */
function NavBar(): ReactElement {
  const content = useContent();
  const locale = useLocale();

  const [isScrolled, setIsScrolled] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const firstDrawerLinkRef = useRef<HTMLAnchorElement>(null);

  const links = visibleNavLinks(content.nav.links, { portfolio: __FEATURE_PORTFOLIO__ });
  const navLinkIds = links.map((link) => link.id);
  const activeSection = useScrollSpy(navLinkIds, { offsetPx: 80 });

  // Defined early so the Escape-key effect can reference it before declaration
  // via the effect's stable ref. The actual function is defined further below
  // but this ref allows the effect to call the latest version without needing
  // closeDrawer in its dependency array.
  const closeDrawerRef = useRef<() => void>(() => undefined);

  // ── Scroll listener ──────────────────────────────────────────────────────
  useEffect(() => {
    function handleScroll() {
      setIsScrolled(window.scrollY > 80);
    }

    // Set immediately in case user loads with a hash anchor
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // ── Body scroll lock while drawer is open ───────────────────────────────
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden';
      // Move focus to first link when drawer opens
      firstDrawerLinkRef.current?.focus();
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isDrawerOpen]);

  // ── Escape key closes drawer ─────────────────────────────────────────────
  useEffect(() => {
    if (!isDrawerOpen) return;

    function handleKeyDown(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape') {
        closeDrawerRef.current();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDrawerOpen]);

  // ── Focus trap in drawer ─────────────────────────────────────────────────
  const handleDrawerKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !drawerRef.current) return;

    const focusable = Array.from(
      drawerRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.hasAttribute('disabled'));

    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    }
  }, []);

  const openDrawer = useCallback(() => {
    setIsDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    // Return focus to hamburger button
    hamburgerRef.current?.focus();
  }, []);

  // Keep the ref in sync with the latest closeDrawer implementation.
  // useLayoutEffect runs synchronously after DOM mutations so the ref
  // is always up-to-date before any paint or event fires.
  useLayoutEffect(() => {
    closeDrawerRef.current = closeDrawer;
  });

  const homePath = localeToPath(locale);

  const drawerId = 'nav-drawer';

  return (
    <>
      {/* ── Nav bar ─────────────────────────────────────────────────────── */}
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-50',
          'h-16 md:h-20',
          'transition-[background-color,backdrop-filter,border-color] duration-300',
          isScrolled
            ? [
                'bg-[var(--color-deep-panel)]/95',
                'backdrop-blur-md',
                'border-b border-[rgba(232,236,244,0.05)]',
              ]
            : 'bg-transparent border-b border-transparent'
        )}
      >
        <Container className="h-full flex items-center justify-between gap-4">
          {/* Logo */}
          <a
            href={homePath}
            className={cn(
              'flex-shrink-0',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-cyan-signal)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-void-navy)]',
              'rounded-sm'
            )}
            aria-label="ARPA — home"
          >
            <img src={logoUrl} alt="ARPA" className="h-8 md:h-9 w-auto" />
          </a>

          {/* Desktop: centred nav links */}
          <nav aria-label="Primary" className="hidden lg:flex items-center gap-6 xl:gap-8">
            {links.map((link) => {
              const isActive = activeSection === link.id;
              return (
                <a
                  key={link.id}
                  href={`#${link.id}`}
                  className={cn(
                    'relative text-sm font-display font-semibold tracking-widest uppercase',
                    'transition-colors duration-150',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-cyan-signal)] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
                    'rounded-sm py-1',
                    isActive
                      ? 'text-[var(--color-cyan-signal)]'
                      : 'text-[var(--color-steel-mist)] hover:text-[var(--color-frost-white)]',
                    isActive && [
                      'after:absolute after:bottom-0 after:left-0 after:right-0',
                      'after:h-px after:bg-[var(--color-cyan-signal)]',
                    ]
                  )}
                  aria-current={isActive ? 'true' : undefined}
                >
                  {link.label}
                </a>
              );
            })}
          </nav>

          {/* Desktop: LanguageSwitcher + CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <LanguageSwitcher />
            <Button href="#contact" variant="primary" size="sm" className="whitespace-nowrap">
              {content.nav.cta}
            </Button>
          </div>

          {/* Mobile: hamburger button */}
          <button
            ref={hamburgerRef}
            type="button"
            aria-label="Open navigation menu"
            aria-expanded={isDrawerOpen}
            aria-controls={drawerId}
            onClick={openDrawer}
            className={cn(
              'lg:hidden p-2 -mr-2',
              'text-[var(--color-frost-white)] hover:text-[var(--color-cyan-signal)]',
              'transition-colors duration-150',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-cyan-signal)] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
              'rounded-sm'
            )}
          >
            <Menu size={24} strokeWidth={1.5} aria-hidden="true" />
          </button>
        </Container>
      </header>

      {/* ── Mobile drawer backdrop ───────────────────────────────────────── */}
      {isDrawerOpen && (
        // Visually-hidden button acts as the click-outside backdrop.
        // Using a <button> satisfies a11y requirements; it is visually inert
        // (aria-hidden keeps screen readers from announcing it) but lets
        // pointer and keyboard users dismiss the drawer by clicking the area
        // behind it. The button is hidden from the accessible tree so focus
        // management is handled by the drawer's focus trap.
        <button
          type="button"
          aria-label="Close navigation menu"
          aria-hidden="true"
          tabIndex={-1}
          className="fixed inset-0 z-[55] bg-[var(--color-void-navy)]/60 backdrop-blur-sm lg:hidden cursor-default"
          onClick={closeDrawer}
        />
      )}

      {/* ── Mobile drawer ───────────────────────────────────────────────── */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions --
          role="dialog" is a composite/landmark role; the linter rule incorrectly
          flags it as non-interactive. onKeyDown is the focus-trap handler. */}
      <div
        id={drawerId}
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        onKeyDown={handleDrawerKeyDown}
        className={cn(
          'fixed inset-y-0 right-0 z-[60] w-full max-w-xs',
          'bg-[var(--color-deep-panel)]',
          'flex flex-col',
          'lg:hidden',
          'transform transition-transform duration-300',
          isDrawerOpen ? 'translate-x-0' : 'translate-x-full',
          isDrawerOpen ? 'visible' : 'invisible'
        )}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-6 h-16 border-b border-[rgba(232,236,244,0.05)]">
          <a
            href={homePath}
            aria-label="ARPA — home"
            onClick={closeDrawer}
            className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-cyan-signal)] rounded-sm"
          >
            <img src={logoUrl} alt="ARPA" className="h-7 w-auto" />
          </a>
          <button
            type="button"
            aria-label="Close navigation menu"
            onClick={closeDrawer}
            className={cn(
              'p-2 -mr-2',
              'text-[var(--color-frost-white)] hover:text-[var(--color-cyan-signal)]',
              'transition-colors duration-150',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-cyan-signal)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-deep-panel)]',
              'rounded-sm'
            )}
          >
            <X size={24} strokeWidth={1.5} aria-hidden="true" />
          </button>
        </div>

        {/* Drawer nav links */}
        <nav aria-label="Primary" className="flex-1 overflow-y-auto px-6 py-8">
          <ul className="flex flex-col gap-2">
            {links.map((link, index) => {
              const isActive = activeSection === link.id;
              return (
                <li key={link.id}>
                  <a
                    ref={index === 0 ? firstDrawerLinkRef : undefined}
                    href={`#${link.id}`}
                    onClick={closeDrawer}
                    aria-current={isActive ? 'true' : undefined}
                    className={cn(
                      'block py-3 px-2',
                      'text-base font-display font-semibold tracking-widest uppercase',
                      'border-b border-[rgba(232,236,244,0.05)]',
                      'transition-colors duration-150',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-cyan-signal)] rounded-sm',
                      isActive
                        ? 'text-[var(--color-cyan-signal)]'
                        : 'text-[var(--color-frost-white)] hover:text-[var(--color-cyan-signal)]'
                    )}
                  >
                    {link.label}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Drawer footer: LanguageSwitcher + CTA */}
        <div className="px-6 pb-8 pt-4 border-t border-[rgba(232,236,244,0.05)] flex flex-col gap-4">
          <LanguageSwitcher />
          <Button href="#contact" variant="primary" size="md" fullWidth onClick={closeDrawer}>
            {content.nav.cta}
          </Button>
        </div>
      </div>
    </>
  );
}

export { NavBar };
export default NavBar;
